// client/src/pages/Game/Battlefield/components/EnhancedLifePoints.jsx

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { subscribeLifeDisplay } from '../utils/lifeDisplay';

/**
 * Cercle de Points de Vie avec cristaux d'énergie autour.
 *
 * Un SEUL cercle. Un SEUL paramètre de taille: le rayon (en cqw).
 * Le DevPanel ne contrôle que "width" qu'on utilise comme rayon.
 * "height" est ignoré — toujours = width pour un cercle parfait.
 *
 * @param {Object} position  Position en % {x, y} du frame
 * @param {Object} size      Taille: {width} utilisé comme rayon en cqw
 */
const EnhancedLifePoints = ({
  player,
  gameState,
  position,
  size,
  onDevDragStart,
  onGemMove,
  devMode
}) => {
  // R34s : drag individuel des gemmes en mode dev (hooks avant early return)
  const lifeElRef = useRef(null);
  const gemDragRef = useRef(null);

  // Déplacer une gemme : la position absolue (base + delta px→cqw) est
  // remontée via onGemMove. Conversion auto-contained : le cercle fait
  // size.width cqw de large → largeur px / width = 1 cqw en px.
  const handleGemDragStart = useCallback((e, gemId) => {
    if (!devMode || !onGemMove) return;
    e.stopPropagation();
    e.preventDefault();
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    if (cx === undefined) return;
    const el = e.currentTarget;
    gemDragRef.current = {
      gemId,
      startX: cx,
      startY: cy,
      baseX: parseFloat(el.dataset.gemX) || 0,
      baseY: parseFloat(el.dataset.gemY) || 0,
    };
    const move = (ev) => {
      const d = gemDragRef.current;
      if (!d) return;
      const mx = ev.touches?.[0]?.clientX ?? ev.clientX;
      const my = ev.touches?.[0]?.clientY ?? ev.clientY;
      const cqwPx = (lifeElRef.current?.getBoundingClientRect().width || 1) / (parseFloat(size.width) || 11);
      onGemMove(d.gemId, {
        x: d.baseX + (mx - d.startX) / cqwPx,
        y: d.baseY + (my - d.startY) / cqwPx,
      });
    };
    const up = () => {
      gemDragRef.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
  }, [devMode, onGemMove, size.width]);

  // R34v : pendant les animations de combat, useCombatAnimations pilote
  // les PV affichés attaque par attaque (l'état serveur est déjà final).
  const [lifeOverride, setLifeOverride] = useState(null);
  useEffect(() => subscribeLifeDisplay(player, setLifeOverride), [player]);

  if (!gameState) return null;

  const lifePoints = (lifeOverride !== null ? lifeOverride : null)
    ?? (player === 'player' ? gameState.playerLife : gameState.opponentLife);
  const energy = player === 'player' ? (gameState.playerEnergy || 0) : (gameState.opponentEnergy || 0);
  // R34u : les perles affichent les GEMMES (SpellMana = énergie non
  // dépensée convertie en fin de tour, max 3), pas l'ancien pool Crystals.
  const gems = player === 'player'
    ? (gameState.playerSpellMana || 0)
    : (gameState.opponentSpellMana || 0);

  // Un seul paramètre: le diamètre du cercle (= size.width en cqw)
  const diameter = size.width;

  // Tailles & espacements des cristaux d'énergie et des perles de mana
  // (éditables via DevPanel — sliders « Cristaux & gemmes »)
  const crystalSize = parseFloat(size.crystalSize ?? 1.2);   // largeur en cqw (hauteur = 1.5x)
  const pearlSize = parseFloat(size.pearlSize ?? 1);         // diamètre en cqw
  // Rayon de l'anneau de cristaux : espace entre le cercle de PV et les cristaux
  const crystalRingGap = parseFloat(size.crystalRingGap ?? 0.3); // marge additionnelle en cqw
  // Perles : distance au cercle + espacement entre perles
  const pearlGap = parseFloat(size.pearlGap ?? 0);           // distance sous le cercle en cqw
  const pearlSpacing = parseFloat(size.pearlSpacing ?? 0.4); // × pearlSize entre les perles

  // Couleurs selon le joueur
  const glowColor = player === 'player' ? '#5ACDFF' : '#FF5A5A';
  const glowColorDark = player === 'player' ? '#0066cc' : '#cc3333';

  // Générer les cristaux d'énergie autour du cercle
  const renderEnergyCrystals = () => {
    const result = [];
    const numCrystals = 10;
    // Rayon de l'anneau = rayon du cercle + taille cristal + marge réglable
    const crystalRingRadius = diameter / 2 + crystalSize + crystalRingGap; // en cqw

    // R34s : overrides individuels (mode dev) — coordonnées ABSOLUES en cqw
    // relatives au centre du cercle, stockées dans size.gems[gemId].
    const gemOverrides = size.gems || {};
    for (let i = 0; i < numCrystals; i++) {
      const gemId = 'ec-' + i;
      const angle = (i * 2 * Math.PI / numCrystals) - Math.PI / 2;
      let x = crystalRingRadius * Math.cos(angle); // cqw
      let y = crystalRingRadius * Math.sin(angle); // cqw
      if (gemOverrides[gemId]) { x = gemOverrides[gemId].x; y = gemOverrides[gemId].y; }
      const isFilled = i < energy;

      result.push(
        <div
          key={gemId}
          data-gem-x={x}
          data-gem-y={y}
          onMouseDown={devMode ? (e) => handleGemDragStart(e, gemId) : undefined}
          onTouchStart={devMode ? (e) => handleGemDragStart(e, gemId) : undefined}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: `${crystalSize}cqw`,
            height: `${crystalSize * 1.5}cqw`,
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            background: isFilled
              ? 'radial-gradient(circle at 30% 30%, #80c8ff, #0066cc)'
              : 'radial-gradient(ellipse at center, #555, #333)',
            opacity: isFilled ? 1 : 0.25,
            boxShadow: isFilled ? `0 0 0.8cqw ${glowColor}` : 'none',
            transform: `translate(calc(-50% + ${x}cqw), calc(-50% + ${y}cqw))`,
            transition: 'all 0.3s ease',
            zIndex: 12,
            cursor: devMode ? 'grab' : 'default',
            outline: devMode ? '1px dashed rgba(90,205,255,0.6)' : 'none',
            animation: isFilled ? 'crystal-pulse 3s infinite alternate ease-in-out' : 'none',
          }}
        />
      );
    }
    return result;
  };

  // Générer les perles de mana — R34s : chaque perle est positionnée
  // individuellement (cqw depuis le centre du cercle) et déplaçable au drag
  // en mode dev, exactement comme les cristaux d'énergie.
  const renderManaPearls = () => {
    const perls = [];
    const maxPearls = 3;
    const gemOverrides = size.gems || {};
    const gap = pearlSize * pearlSpacing;
    const totalW = maxPearls * pearlSize + (maxPearls - 1) * gap;
    // Distance verticale du centre (sous le cercle pour player, au-dessus pour opponent)
    const ringDist = diameter / 2 + diameter * 0.15 + pearlGap + pearlSize / 2;
    for (let i = 0; i < maxPearls; i++) {
      const gemId = 'mp-' + i;
      let x = -totalW / 2 + pearlSize / 2 + i * (pearlSize + gap);
      let y = player === 'opponent' ? -ringDist : ringDist;
      if (gemOverrides[gemId]) { x = gemOverrides[gemId].x; y = gemOverrides[gemId].y; }
      const isFilled = i < gems;
      perls.push(
        <div
          key={gemId}
          data-gem-x={x}
          data-gem-y={y}
          onMouseDown={devMode ? (e) => handleGemDragStart(e, gemId) : undefined}
          onTouchStart={devMode ? (e) => handleGemDragStart(e, gemId) : undefined}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: `${pearlSize}cqw`,
            height: `${pearlSize}cqw`,
            borderRadius: '50%',
            background: isFilled
              ? 'radial-gradient(circle at 30% 30%, #ffffff, #e0e0ff)'
              : 'radial-gradient(circle at 30% 30%, #444, #222)',
            opacity: isFilled ? 1 : 0.25,
            boxShadow: isFilled ? '0 0 0.6cqw #ffffff, 0 0 1cqw #e0e0ff' : 'none',
            animation: isFilled ? 'pearl-pulse 3s infinite alternate ease-in-out' : 'none',
            transition: 'all 0.3s ease',
            transform: `translate(calc(-50% + ${x}cqw), calc(-50% + ${y}cqw))`,
            zIndex: 12,
            cursor: devMode ? 'grab' : 'default',
            outline: devMode ? '1px dashed rgba(255,255,255,0.5)' : 'none',
          }}
        />
      );
    }
    return perls;
  };

  const handleDevDrag = (e) => {
    if (devMode && onDevDragStart) onDevDragStart(e);
  };

  return (
    <>
      <style>{`
        @keyframes crystal-pulse {
          0% { filter: brightness(0.9); }
          50% { filter: brightness(1.3); }
          100% { filter: brightness(0.9); }
        }
        @keyframes pearl-pulse {
          0% { filter: brightness(0.9); }
          50% { filter: brightness(1.3); }
          100% { filter: brightness(0.9); }
        }
      `}</style>

      <div
        ref={lifeElRef}
        className={`game-element ${player}-life`}
        style={{
          // Position en % du frame
          position: 'absolute',
          left: `${position.x}%`,
          top: `${position.y}%`,
          // Taille: cercle parfait — width = height = diameter
          width: `${diameter}cqw`,
          height: `${diameter}cqw`,
          transform: 'translate(-50%, -50%)',
          zIndex: 5,
          cursor: devMode ? 'move' : 'default',
        }}
        onMouseDown={devMode ? handleDevDrag : undefined}
        onTouchStart={devMode ? handleDevDrag : undefined}
      >
        {/* Cristaux d'énergie autour du cercle */}
        {renderEnergyCrystals()}

        {/* Le cercle lui-même */}
        <div style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 35%, rgba(40,40,60,0.95), rgba(0,0,0,0.95))',
          border: `0.3cqw solid ${glowColor}`,
          boxShadow: `0 0 1cqw ${glowColor}, 0 0 2cqw ${glowColorDark}, inset 0 0 1cqw rgba(0,0,0,0.8)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 5,
        }}>
          <span style={{
            color: '#fff',
            fontSize: `calc(${diameter * 0.35}cqw * var(--font-scale, 1) * var(--fs-life, 1))`,
            fontWeight: 'bold',
            textShadow: '0 0 0.3cqw rgba(0,0,0,0.9)',
            fontFamily: '"Cinzel", serif',
          }}>
            {lifePoints}
          </span>
        </div>

        {/* Perles de mana — R34s : positionnées individuellement */}
        {renderManaPearls()}

        {/* Bordure dev mode */}
        {devMode && (
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '1px dashed rgba(255,255,255,0.4)',
            pointerEvents: 'none',
            zIndex: 10,
          }} />
        )}
      </div>
    </>
  );
};

export default EnhancedLifePoints;
